import { z } from "zod";

import { calculationRunSummarySchema } from "./calculationRun";
import { jurisdictionProfileSchema } from "./cableContract";
import { exactDecimalSchema } from "./faultContract";
import { CHOOSE_A_FAULT_RUN, formatApiError, requestSignal } from "./faultLink";
import { ApiError } from "./http";
import { CHOOSE_A_LOAD_RUN } from "./loadLink";
import {
  faultTypeSchema,
  loadFiguresSourceSchema,
  powerQualityStatusSchema,
  powerQualityWarningCodeSchema,
  shortCircuitCaseSchema,
  shortCircuitPowerSourceSchema,
} from "./powerQualityContract";

// The power factor correction study (EOS-11a, Master Prompt A32): the request as the form
// sends it, the response as the engine answers it, and the persisted run. The backend
// mirror is app/schemas/power_quality.py (PfCorrectionRequest, PfCorrectionResponse).

const optionalRequestText = (maxLength: number) =>
  z.string().trim().max(maxLength).nullable().optional();

const RUNS_URL = "/api/v1/electrical/pf-correction/runs";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// A harmonic order: an integer of 2 or more (the backend takes up to 100).
export const harmonicOrderSchema = z.number().int().min(2).max(100);

// The load figures typed by the engineer: allowed, but not linked evidence; a blank source
// makes the study REVIEW_REQUIRED (the backend says so).
export const typedLoadFiguresSchema = z
  .object({
    active_power_kw: exactDecimalSchema,
    present_power_factor: exactDecimalSchema,
    source: optionalRequestText(200),
  })
  .strict();

// A bus short-circuit power typed by the engineer, on the U^2 / |Z_k| basis (R2).
export const typedShortCircuitPowerSchema = z
  .object({
    short_circuit_power_kva: exactDecimalSchema,
    source: optionalRequestText(200),
  })
  .strict();

// The bank as declared (D7): its rated voltage, an optional rated kvar and the APFC steps.
export const capacitorBankRequestSchema = z
  .object({
    rated_voltage_v: exactDecimalSchema,
    declared_rating_kvar: exactDecimalSchema.optional(),
    step_sizes_kvar: z.array(exactDecimalSchema).max(24).optional(),
  })
  .strict();

export const pfCorrectionRequestSchema = z
  .object({
    code: z.string().trim().min(1).max(50),
    name: z.string().trim().min(1).max(200),
    bus_code: z.string().trim().min(1).max(50),
    system_nominal_voltage_v: exactDecimalSchema,
    frequency_hz: exactDecimalSchema,
    // Exactly one of the two: a stored EOS-02 load run of the organization, or typed figures.
    load_run_id: z.string().optional(),
    typed_load: typedLoadFiguresSchema.optional(),
    // The target has no default (D3).
    target_power_factor: exactDecimalSchema,
    bank: capacitorBankRequestSchema,
    reactor_ratio_percent: exactDecimalSchema.optional(),
    // At most one of the two; neither means the resonance screening is not made.
    fault_run_id: z.string().optional(),
    typed_short_circuit_power: typedShortCircuitPowerSchema.optional(),
    characteristic_orders: z.array(harmonicOrderSchema).max(50).optional(),
    jurisdiction_profile: jurisdictionProfileSchema.optional(),
    notes: optionalRequestText(1000),
  })
  .strict()
  .superRefine((values, ctx) => {
    if ((values.load_run_id === undefined) === (values.typed_load === undefined)) {
      ctx.addIssue({
        code: "custom",
        message: "give exactly one load figures source: load_run_id or typed_load",
        path: ["load_run_id"],
      });
    } else if (values.load_run_id !== undefined && !UUID.test(values.load_run_id)) {
      ctx.addIssue({ code: "custom", message: CHOOSE_A_LOAD_RUN, path: ["load_run_id"] });
    }

    if (values.fault_run_id !== undefined && values.typed_short_circuit_power !== undefined) {
      ctx.addIssue({
        code: "custom",
        message:
          "give at most one short-circuit power source: fault_run_id or typed_short_circuit_power",
        path: ["fault_run_id"],
      });
    } else if (values.fault_run_id !== undefined && !UUID.test(values.fault_run_id)) {
      ctx.addIssue({ code: "custom", message: CHOOSE_A_FAULT_RUN, path: ["fault_run_id"] });
    }

    const orders = values.characteristic_orders ?? [];
    if (new Set(orders).size !== orders.length) {
      ctx.addIssue({
        code: "custom",
        message: "characteristic_orders must not repeat an order",
        path: ["characteristic_orders"],
      });
    }
  });

export const powerQualityWarningSchema = z
  .object({
    code: powerQualityWarningCodeSchema,
    message: z.string(),
  })
  .strict();

export const linkedLoadRunResponseSchema = z
  .object({
    load_run_id: z.string().uuid(),
    group_code: z.string(),
    revision_number: z.number().int(),
  })
  .strict();

// The bus short-circuit power with where it came from and how it was formed (R2): the fault
// run's id, case, type and bus for a linked figure (null when typed), the engineer's source
// text for a typed one, and the run's Ik'' when the run held it.
export const shortCircuitPowerResponseSchema = z
  .object({
    value_kva: z.string(),
    source: shortCircuitPowerSourceSchema,
    basis: z.string(),
    fault_run_id: z.string().uuid().nullable(),
    calculation_case: shortCircuitCaseSchema.nullable(),
    fault_type: faultTypeSchema.nullable(),
    fault_bus_code: z.string().nullable(),
    source_text: z.string().nullable(),
    short_circuit_current_ka: z.string().nullable(),
  })
  .strict();

export const capacitorBankResponseSchema = z
  .object({
    rated_voltage_v: z.string(),
    declared_rating_kvar: z.string().nullable(),
    step_sizes_kvar: z.array(z.string()),
  })
  .strict();

export const stepLayoutResponseSchema = z
  .object({
    steps_kvar: z.array(z.string()),
    smallest_step_share_percent: z.string(),
    reachable_kvar: z.string(),
    residual_kvar: z.string(),
  })
  .strict();

export const reactorResponseSchema = z
  .object({
    reactor_ratio_percent: z.string(),
    tuning_order: z.string(),
    capacitor_terminal_voltage_v: z.string(),
    capacitor_voltage_within_rating: z.boolean(),
  })
  .strict();

// The resonance screening on the R2 basis: every characteristic order whose ±10 % band
// contains h_r or h_p is listed (matched_orders), beside the nearest order and its distance.
export const resonanceResponseSchema = z
  .object({
    short_circuit_power_kva: z.string(),
    short_circuit_power_basis: z.string(),
    resonance_order: z.string(),
    detuned_resonance_order: z.string().nullable(),
    nearest_order: z.number().int().nullable(),
    distance_to_nearest_order: z.string().nullable(),
    matched_orders: z.array(z.number().int()),
    near_characteristic_order: z.boolean(),
  })
  .strict();

// Every input echoed with where it came from, each figure the engine's exact string, the
// blocks null when not made (no bank declared, no steps, no reactor, no short-circuit power).
export const pfCorrectionResponseSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    bus_code: z.string(),
    system_nominal_voltage_v: z.string(),
    frequency_hz: z.string(),
    active_power_kw: z.string(),
    present_power_factor: z.string(),
    target_power_factor: z.string(),
    load_source: loadFiguresSourceSchema,
    load_source_text: z.string().nullable(),
    linked_load_run: linkedLoadRunResponseSchema.nullable(),
    bank: capacitorBankResponseSchema,
    reactor_ratio_percent: z.string().nullable(),
    short_circuit_power: shortCircuitPowerResponseSchema.nullable(),
    characteristic_orders: z.array(z.number().int()),
    tan_phi_present: z.string(),
    tan_phi_target: z.string(),
    apparent_power_kva_before: z.string(),
    reactive_power_kvar_before: z.string(),
    required_compensation_kvar: z.string(),
    apparent_power_kva_after: z.string(),
    reactive_power_kvar_after: z.string(),
    voltage_ratio_squared: z.string(),
    bank_rating_required_kvar: z.string(),
    bank_rating_required_with_reactor_kvar: z.string().nullable(),
    declared_bank_delivered_kvar: z.string().nullable(),
    bank_covers_requirement: z.boolean().nullable(),
    step_layout: stepLayoutResponseSchema.nullable(),
    reactor: reactorResponseSchema.nullable(),
    resonance: resonanceResponseSchema.nullable(),
    status: powerQualityStatusSchema,
    warnings: z.array(powerQualityWarningSchema),
    billing_context: z.string(),
    power_factor_basis: z.string(),
    jurisdiction_profile: jurisdictionProfileSchema,
    notes: z.string().nullable(),
  })
  .strict();

export const pfCorrectionRunCreateRequestSchema = z
  .object({
    study: pfCorrectionRequestSchema,
    project_revision_id: z.string().uuid().optional(),
    notes: optionalRequestText(2000),
  })
  .strict();

export const pfCorrectionRunResponseSchema = z
  .object({
    run: calculationRunSummarySchema,
    result: pfCorrectionResponseSchema,
  })
  .strict();

export type TypedLoadFigures = z.infer<typeof typedLoadFiguresSchema>;
export type TypedShortCircuitPower = z.infer<typeof typedShortCircuitPowerSchema>;
export type CapacitorBankRequest = z.infer<typeof capacitorBankRequestSchema>;
export type PfCorrectionRequest = z.infer<typeof pfCorrectionRequestSchema>;
export type PowerQualityWarning = z.infer<typeof powerQualityWarningSchema>;
export type ShortCircuitPowerResponse = z.infer<typeof shortCircuitPowerResponseSchema>;
export type ResonanceResponse = z.infer<typeof resonanceResponseSchema>;
export type PfCorrectionResponse = z.infer<typeof pfCorrectionResponseSchema>;
export type PfCorrectionRunCreateRequest = z.infer<typeof pfCorrectionRunCreateRequestSchema>;
export type PfCorrectionRunResponse = z.infer<typeof pfCorrectionRunResponseSchema>;

/**
 * Calculate a power factor correction study and persist it as a run revision.
 *
 * The request carries the whole run body, because a study's notes belong to the run. With
 * a project revision id the run is stored under that revision - and a linked load run or
 * fault run must then belong to the same revision; without one it belongs to no project.
 * An unchanged study returns the stored revision with HTTP 200 (A11); both are valid here.
 */
export async function createPfCorrectionRun(
  payload: PfCorrectionRunCreateRequest,
  signal?: AbortSignal,
  projectRevisionId?: string,
): Promise<PfCorrectionRunResponse> {
  const validatedPayload = pfCorrectionRunCreateRequestSchema.parse(payload);

  const response = await fetch(RUNS_URL, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(
      projectRevisionId === undefined
        ? validatedPayload
        : { ...validatedPayload, project_revision_id: projectRevisionId },
    ),
    cache: "no-store",
    signal: requestSignal(signal),
  });

  const data: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiError(
      formatApiError(data, response.status, "Power factor correction"),
      response.status,
    );
  }

  const parsed = pfCorrectionRunResponseSchema.safeParse(data);

  if (!parsed.success) {
    throw new Error(
      "Unexpected response from the KES Electrical OS power factor correction runs API.",
    );
  }

  return parsed.data;
}
